import { API_BASE_URL } from '../config';

class APIService {
    constructor() {
        this.baseURL = API_BASE_URL;
        console.log('Admin API Service initialized with baseURL:', this.baseURL);
    }

    // Helper method to get auth headers
    getAuthHeaders() {
        const token = localStorage.getItem('accessToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    // Helper method to handle API responses
    async handleResponse(response) {
        if (!response.ok) {
            const error = await response.json().catch(() => ({
                message: `Network error: ${response.status} ${response.statusText}`
            }));
            console.error('Admin API Response Error:', {
                status: response.status,
                statusText: response.statusText,
                url: response.url,
                error: error
            });
            throw new Error(error.detail || error.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }

    // =========================================================================
    // SQAAF Methods
    // =========================================================================

    sqaaf = {
        // Framework metadata
        getFrameworkOverview: async () => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/framework/overview`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        getDomainStructure: async (domainId) => {
            const url = domainId
                ? `${this.baseURL}/admin/sqaaf/framework/domains/${domainId}`
                : `${this.baseURL}/admin/sqaaf/framework/domains`;
            const response = await fetch(url, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Audit management
        getCurrentAudit: async (schoolId, academicYear = null) => {
            const params = `school_id=${schoolId}${academicYear ? `&academic_year=${encodeURIComponent(academicYear)}` : ''}`;
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/current?${params}`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        startAudit: async (schoolId, academicYear) => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/start`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ school_id: schoolId, academic_year: academicYear })
            });
            return this.handleResponse(response);
        },

        submitAudit: async (auditId) => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/submit`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ audit_id: auditId, confirm: true })
            });
            return this.handleResponse(response);
        },

        // Detailed questions for comprehensive quiz mode
        getDetailedQuestions: async (indicatorId) => {
            const url = indicatorId
                ? `${this.baseURL}/admin/sqaaf/framework/detailed-questions/${indicatorId}`
                : `${this.baseURL}/admin/sqaaf/framework/detailed-questions`;
            const response = await fetch(url, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Response saving
        saveResponse: async (auditId, indicatorId, level, evidenceFiles = [], notes = null, quizMode = 'short', detailedResponses = null, extra = {}) => {
            const body = {
                audit_id: auditId,
                indicator_id: indicatorId,
                selected_level: level,
                evidence_files: evidenceFiles,
                notes: notes,
                quiz_mode: quizMode,
            };
            if (detailedResponses) body.detailed_responses = detailedResponses;
            // extra: { partial, resume: { question_index } } — see sqaaf_router save-single
            if (extra.partial) body.partial = true;
            if (extra.resume) body.resume = extra.resume;
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/save-single`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(body)
            });
            return this.handleResponse(response);
        },

        // Evidence Upload
        uploadEvidence: async (indicatorId, fileObj, auditId) => {
            const formData = new FormData();
            formData.append('indicator_id', indicatorId);
            formData.append('file', fileObj);
            if (auditId) formData.append('audit_id', auditId);

            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${this.baseURL}/admin/sqaaf/evidence/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Content-Type header is auto-set by browser with boundary for FormData
                },
                body: formData
            });
            return this.handleResponse(response);
        },

        deleteEvidence: async (fileId) => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/evidence/${fileId}`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Auto-evidence hints from teacher data
        getAutoEvidence: async () => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/auto-evidence`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Action Plan - improvement recommendations
        getActionPlan: async (academicYear = null) => {
            const params = academicYear ? `?academic_year=${academicYear}` : '';
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/action-plan${params}`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Audit History - past audits
        getAuditHistory: async () => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/history`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // AI-Powered Action Plan (Legacy domain-level)
        getAIActionPlan: async (academicYear = null) => {
            const params = academicYear ? `?academic_year=${academicYear}` : '';
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/action-plan/ai${params}`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Start background action plan generation
        startActionPlanGeneration: async (academicYear = null) => {
            const params = academicYear ? `?academic_year=${academicYear}` : '';
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/action-plan/generate${params}`, {
                method: 'POST',
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Check action plan generation status
        getActionPlanStatus: async (jobId) => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/action-plan/status/${jobId}`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Get full generated action plan
        getFullActionPlan: async (academicYear = null) => {
            const params = academicYear ? `?academic_year=${academicYear}` : '';
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/action-plan/full${params}`, {
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        },

        // Restart Audit - archives current and creates new
        restartAudit: async (schoolId) => {
            const response = await fetch(`${this.baseURL}/admin/sqaaf/audit/restart`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ school_id: schoolId })
            });
            return this.handleResponse(response);
        }
    };

    // =========================================================================
    // Authentication Methods
    // =========================================================================

    // Multi-tenant authentication methods
    async validateCredentials(email, password) {
        console.log('Validating admin credentials for:', email);

        const response = await fetch(`${this.baseURL}/auth/validate-credentials`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: email,
                password: password,
                role: 'admin'
            })
        });

        console.log('Credentials validation response:', {
            status: response.status,
            statusText: response.statusText
        });

        return this.handleResponse(response);
    }

    async loginWithSchool(email, password, schoolId) {
        console.log('Logging in with school context:', { email, schoolId });

        const response = await fetch(`${this.baseURL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: email,
                password: password,
                school_id: schoolId,
                role: 'admin'
            })
        });

        console.log('School login response:', {
            status: response.status,
            statusText: response.statusText
        });

        return this.handleResponse(response);
    }

    // Legacy authentication method (fallback) - supports all admin panel roles
    async login(email, password) {
        console.log('Using admin panel login for:', email);

        const response = await fetch(`${this.baseURL}/login/admin-panel`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                username: email,
                password: password
            })
        });

        console.log('Legacy login response:', {
            status: response.status,
            statusText: response.statusText
        });

        return this.handleResponse(response);
    }

    // Test connectivity to the backend
    async testConnection() {
        try {
            const response = await fetch(`${this.baseURL}/health`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Backend connectivity test successful:', data);
                return { success: true, data };
            } else {
                console.error('Backend connectivity test failed:', response.status, response.statusText);
                return { success: false, error: `HTTP ${response.status}` };
            }
        } catch (error) {
            console.error('Backend connectivity test error:', error);
            return { success: false, error: error.message };
        }
    }

    // Enhanced error handling
    handleError(error) {
        console.error('Admin API Error:', error);

        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
            // Clear tokens and redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('school_id');
            localStorage.removeItem('school_name');
            window.location.href = '/';
            return 'Session expired. Please login again.';
        }

        if (error.message.includes('403') || error.message.includes('Forbidden')) {
            return 'Access denied. Insufficient permissions.';
        }

        if (error.message.includes('404') || error.message.includes('Not Found')) {
            return 'Resource not found.';
        }

        if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
            return 'Server error. Please try again later.';
        }

        if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
            return 'Network error. Please check your connection and backend server.';
        }

        return error.message || 'An unexpected error occurred';
    }

    // Utility methods
    isAuthenticated() {
        return !!localStorage.getItem('accessToken');
    }

    logout() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('school_id');
        localStorage.removeItem('school_name');
    }
}

// Create and export a singleton instance
const apiService = new APIService();
export default apiService;